// Description: Context + provider + reducer managing a global toast queue

// Import React and hooks
import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef } from "react";



// Create a React context to expose state and actions
export const ToastContext = createContext(null);

// Define the allowed toast variants for type safety at runtime
const TOAST_VARIANTS = ["success", "error", "warning", "info"];

// Define the initial state for the reducer
const initialState = { toasts: [] };

// Create a simple unique id generator scoped to this module
let uid = 0;



// Reducer to manage toast list changes
function toastReducer(state, action) {

  // Switch over action type to modify state
  switch (action.type) {

    // Add a new toast to the list
    case "ADD": {

      // Return new state with the new toast appended
      return { ...state, toasts: [...state.toasts, action.payload] };

    }

    // Remove a toast by id
    case "REMOVE": {

      // Filter out the toast matching the id
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.id) };

    }

    // Clear all toasts at once
    case "CLEAR": {

      // Return empty list of toasts
      return { ...state, toasts: [] };

    }

    // Default case returns the current state unchanged
    default:

      return state;

  }

}



// Exported provider to wrap your app once (e.g., in App.jsx)
export function ToastProvider({ children, maxVisible = 5 }) {

  // Instantiate reducer for toast state
  const [state, dispatch] = useReducer(toastReducer, initialState);

  // Ref that stores active timers by toast id so we can clear/pause them
  const timersRef = useRef(new Map());

  // Helper to actually schedule auto close
  const scheduleAutoClose = useCallback((id, timeout) => {

    // If timeout is falsy or zero, do not auto close
    if (!timeout || timeout <= 0) return;

    // Clear any prior timer to avoid duplicates
    clearTimeout(timersRef.current.get(id));

    // Create a new timeout and keep a handle
    const handle = setTimeout(() => dispatch({ type: "REMOVE", id }), timeout);

    // Store the handle in the map for later clearing or pausing
    timersRef.current.set(id, handle);

  }, []);

  // Public API: add a toast and return its id
  const addToast = useCallback((toastInput) => {

    // Normalize incoming data with defaults and validation
    const {
      title = "",
      message = "",
      apiErrorMessage = "",
      type = "info",
      autoCloseMs = 4000,
      canUserDismiss = true
    } = toastInput || {};

    // Coerce the variant to one of the allowed values
    const variant = TOAST_VARIANTS.includes(type) ? type : "info";

    // Generate a unique id for this toast
    const id = ++uid;

    // Build a toast object
    const toast = { id, title, message, apiErrorMessage, type: variant, autoCloseMs, canUserDismiss };

    // If we exceed maxVisible, drop oldest to keep stack small
    const overflow = Math.max(0, (state.toasts.length + 1) - maxVisible);

    // Remove the oldest N items when needed
    if (overflow > 0) {

      // Compute ids of the oldest items to remove
      const dropIds = state.toasts.slice(0, overflow).map((t) => t.id);

      // Cancel timers for dropped toasts
      dropIds.forEach((d) => clearTimeout(timersRef.current.get(d)));

      // Dispatch a clear-then-add sequence by rebuilding payloads
      const remaining = state.toasts.filter((t) => !dropIds.includes(t.id));

      // Replace state synchronously by dispatching CLEAR then re-adding remaining and new
      dispatch({ type: "CLEAR" });

      // Re-add remaining items
      remaining.forEach((t) => dispatch({ type: "ADD", payload: t }));

      // Add the new toast
      dispatch({ type: "ADD", payload: toast });

    } else {

      // Simple case where we can append directly
      dispatch({ type: "ADD", payload: toast });

    }

    // Schedule auto close after dispatch
    scheduleAutoClose(id, autoCloseMs);

    // Return id so callers could manually dismiss or update
    return id;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleAutoClose, state.toasts.length, maxVisible]);

  // Public API: remove a toast by id
  const removeToast = useCallback((id) => {

    // Clear any existing timer for this toast
    clearTimeout(timersRef.current.get(id));

    // Remove the timer handle from the map
    timersRef.current.delete(id);

    // Dispatch removal to update UI
    dispatch({ type: "REMOVE", id });

  }, []);

  // Public API: clear all toasts
  const clearToasts = useCallback(() => {

    // Clear timers for all existing toasts
    timersRef.current.forEach((h) => clearTimeout(h));

    // Reset timer map to a fresh instance
    timersRef.current = new Map();

    // Dispatch a clear action to wipe the list
    dispatch({ type: "CLEAR" });

  }, []);

  // Value memo to avoid re-renders of consumers when functions/values are stable
  const value = useMemo(() => ({

    // Expose the raw toasts array for renderers
    toasts: state.toasts,

    // Expose action creators
    addToast,
    removeToast,
    clearToasts

  }), [state.toasts, addToast, removeToast, clearToasts]);

  // Render provider with children below
  return (

    <ToastContext.Provider value={value}>

      {children}

    </ToastContext.Provider>

  );

}



// Convenience hook that throws when used outside the provider
export function useToastContext() {

  // Read current context value
  const ctx = useContext(ToastContext);

  // Ensure the provider is present in the tree
  if (!ctx) throw new Error("useToastContext must be used within <ToastProvider>");

  // Return the context value for consumers
  return ctx;

}

